enum RenderStrategies {
    default = 'default',
    ondemand = 'ondemand',
    serverside = 'serverside'
}

export type RenderStrategiesTypes = keyof typeof RenderStrategies;

export abstract class LazyRenderer {
    protected abstract getNodeAttribute(node: HTMLElement, name: string): string | null;
    private intersectionObserver: IntersectionObserver;
    private viewHeight: number;
    private ALLOWED_PIXELS = 200;
    private pendingNodes: Set<HTMLElement> = new Set([]);

    private updateViewHeight = () => {
        this.viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    };

    private isInViewport = (node: HTMLElement): boolean => {
        const { bottom, top } = node.getBoundingClientRect();
        if (!this.viewHeight) {
            this.updateViewHeight();
        }
        // To load before get's into screen (scrolling down)
        const isBottomLessThanViewport = bottom < this.viewHeight + this.ALLOWED_PIXELS;
        // Check if its on viewport or close to it
        const isBottomInViewport = bottom > -this.ALLOWED_PIXELS;

        const isBottomVisible = isBottomLessThanViewport && isBottomInViewport;

        // In case it is scrolling up to start rendering
        const isTopVisible = top + this.ALLOWED_PIXELS > 0 && top < this.viewHeight;

        // In case node is higher than viewport
        const isHigherThanViewHeight = bottom < 0 && top > this.viewHeight;

        return isBottomVisible || isTopVisible || isHigherThanViewHeight;
    };

    private getRenderMethodStrategy = (node: HTMLElement) =>
        <RenderStrategiesTypes>this.getNodeAttribute(node, 'data-render-method') ||
        RenderStrategies.default;

    private isNodeInViewport = (node: HTMLElement): boolean => {
        if (!this.isInViewport(node)) {
            this.pendingNodes.add(node);

            return false;
        }

        return true;
    };

    public shouldRenderNode = (node: HTMLElement): boolean => {
        const renderStrategy: RenderStrategiesTypes = this.getRenderMethodStrategy(node);

        if (renderStrategy === RenderStrategies.ondemand) {
            return this.isNodeInViewport(node);
        }

        return true;
    };

    public initLazyRenderer = (onNodeDisplay: (node: Element) => void) => {
        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0
        };

        const intersectionCallback = ([entry]: IntersectionObserverEntry[]) => {
            if (entry.isIntersecting) {
                const target = entry.target as HTMLElement;

                this.pendingNodes.delete(target);
                this.intersectionObserver.unobserve(target);
                // No more nodes to observe
                if (this.pendingNodes.size === 0) {
                    this.intersectionObserver.disconnect();
                    // Setting the property as an optional `null` in the interface would add more checks and that could end up in a bug
                    // @ts-ignore
                    this.intersectionObserver = null;
                }
                onNodeDisplay(target);
            }
        };

        this.intersectionObserver = new IntersectionObserver(intersectionCallback, options);
        this.pendingNodes.forEach(node => this.intersectionObserver.observe(node));
    };
}
